import {
  Avatar,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Chip,
  Skeleton,
  Container,
  IconButton,
  InputAdornment
} from '@mui/material'
import { Search, Clear, Phone, Person, Email } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import useFetch from '../../hooks/useFetch'
import { urlApi } from '../../config'
import UserInfo from './UserInfo'

// iOS/Android envuelven los teléfonos copiados con marcas bidi invisibles (U+202A, U+200E, etc.)
const INVISIBLE_CHARS = /[\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF]/g

const normalizeSearchInput = raw => {
  let val = (raw || '').replace(INVISIBLE_CHARS, '')

  const compact = val.replace(/[\s\-‐‑‒–—().]/g, '')
  if (/^\+?\d+$/.test(compact)) {
    val = compact
  }

  if (val.startsWith('+549')) {
    val = val.replace('+549', '+54')
  }

  return val
}

// user_id de la URL: "%2B+549..." se decodifica como "+ 549..." (el "+" literal pasa a espacio)
const normalizeDefaultId = raw => {
  let val = (raw || '').replace(INVISIBLE_CHARS, '').replace(/\s+/g, '')
  if (/^\+?\d+$/.test(val) && !val.startsWith('+')) {
    val = '+' + val
  }
  return normalizeSearchInput(val)
}

const UserSearch = ({ defaultId }) => {
  const initialText = normalizeDefaultId(defaultId)
  const [text, setText] = useState(initialText)
  const [search, setSearch] = useState(initialText ? true : false)

  const handleSearch = () => {
    setSearch(false)
    setTimeout(() => {
      setSearch(true)
    }, 100)
  }

  const handleClear = () => {
    setText('')
    setSearch(false)
  }

  return (
    <Container
      maxWidth='lg'
      sx={{
        height: 'calc(100vh - 4rem)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 2, sm: 2 }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2, mt: 2 }}
      >
        <TextField
          label='Usuario'
          variant='outlined'
          value={text}
          onChange={e => setText(normalizeSearchInput(e.target.value))}
          // Sólo el Enter de este campo busca: escuchar en document pisaba el Enter de los
          // diálogos del perfil (p. ej. guardar el comentario) y recargaba la búsqueda.
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey && text.trim()) handleSearch()
          }}
          placeholder='Ingresá ID, teléfono o nombre...'
          fullWidth
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Person color='action' />
              </InputAdornment>
            ),
            endAdornment: text && (
              <InputAdornment position='end'>
                <IconButton onClick={handleClear} size='small'>
                  <Clear />
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Stack
          direction={{ xs: 'row', sm: 'row' }}
          spacing={1}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <Button
            variant='outlined'
            color='error'
            onClick={handleClear}
            sx={{
              minWidth: { xs: 'auto', sm: 120 },
              flex: { xs: 1, sm: 'none' },
              height: 45
            }}
          >
            Cancelar
          </Button>
          <Button
            variant='contained'
            onClick={handleSearch}
            startIcon={<Search />}
            disabled={!text.trim()}
            sx={{
              minWidth: { xs: 'auto', sm: 120 },
              flex: { xs: 1, sm: 'none' },
              height: 45
            }}
          >
            Buscar
          </Button>
        </Stack>
      </Stack>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {search && <UsersRequest text={text} defaultId={defaultId} />}
      </Box>
    </Container>
  )
}

const UsersRequest = ({ text, defaultId }) => {
  const { loading, data } = useFetch(`${urlApi}/admin/users/${text}`)
  const [userInfo, setUserInfo] = useState()

  useEffect(() => {
    if (defaultId && data.length > 0) {
      setUserInfo(data[0])
    }
  }, [data])

  if (loading) {
    return (
      <Stack spacing={2}>
        {[...Array(3)].map((_, index) => (
          <Card key={index} elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack direction='row' spacing={2} alignItems='center'>
                <Skeleton variant='circular' width={56} height={56} />
                <Stack flex={1} spacing={1}>
                  <Skeleton variant='text' width='60%' height={24} />
                  <Skeleton variant='text' width='40%' height={20} />
                </Stack>
                <Skeleton variant='text' width={100} height={20} />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    )
  }

  if (userInfo) {
    return <UserInfo userInfo={userInfo} setUserInfo={setUserInfo} />
  }

  if (data.status === 404 || data.status === 500) {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, textAlign: 'center', py: 4 }}>
        <CardContent>
          <Typography variant='h6' color='error' gutterBottom>
            Error en la búsqueda
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Ha ocurrido un error. Por favor intenta nuevamente.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (data.length === 0) {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, textAlign: 'center', py: 4 }}>
        <CardContent>
          <Typography variant='h6' color='text.secondary' gutterBottom>
            No se encontró ningún usuario
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Intenta con un término de búsqueda diferente.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Stack spacing={2} sx={{ pb: 2 }}>
      {data.map((user, index) => (
        <Card
          key={index}
          elevation={1}
          sx={{
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.3s ease-in-out',
            borderColor: 'divider',
            width: '99%',
            alignSelf: 'center'
          }}
          onClick={() => setUserInfo(user)}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Stack direction='row' spacing={2} alignItems='center' flex={1}>
                <Box
                  sx={{
                    'position': 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: -2,
                      left: -2,
                      right: -2,
                      bottom: -2,
                      borderRadius: '50%',
                      zIndex: -1,
                      opacity: 0,
                      transition: 'opacity 0.3s ease'
                    },
                    '&:hover::after': {
                      opacity: 1
                    }
                  }}
                >
                  <Avatar
                    src={user.picture}
                    className='user-avatar'
                    sx={{
                      width: { xs: 50, md: 64 },
                      height: { xs: 50, md: 64 },
                      transition: 'transform 0.3s ease'
                    }}
                  />
                </Box>
                <Stack spacing={1} flex={1}>
                  <Typography
                    variant='h6'
                    fontWeight={700}
                    sx={{
                      color: 'text.primary',
                      fontSize: { xs: '1.25rem', md: '1.25rem' }
                    }}
                  >
                    {user?.user_metadata?.first_name} {user?.user_metadata?.last_name}
                  </Typography>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Typography
                      variant='body2'
                      sx={{
                        borderRadius: 1,
                        fontSize: { xs: '0.75rem', md: '0.85rem' }
                      }}
                    >
                      {user?.user_id}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>

              {user?.user_metadata?.phone_number && (
                <Chip
                  icon={<Phone fontSize='small' />}
                  label={user.user_metadata.phone_number}
                  variant='outlined'
                  size='medium'
                  sx={{
                    alignSelf: { xs: 'flex-start', sm: 'center' },
                    ml: { xs: 0, sm: 'auto' },
                    px: 1,
                    fontWeight: 600
                  }}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  )
}

export default UserSearch
